import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Shell from "../components/Shell";
import { api } from "../lib/api";
import { ArrowLeft, Bell, Check } from "lucide-react";

function groupByDay(items) {
  const groups = {};
  items.forEach((n) => {
    const d = new Date(n.created_at);
    const today = new Date();
    const yest = new Date(); yest.setDate(today.getDate() - 1);
    let key;
    if (d.toDateString() === today.toDateString()) key = "Today";
    else if (d.toDateString() === yest.toDateString()) key = "Yesterday";
    else key = d.toLocaleDateString(undefined, { month: "long", day: "numeric" });
    (groups[key] ||= []).push(n);
  });
  return groups;
}

export default function NotificationsScreen() {
  const [items, setItems] = useState([]);
  const navigate = useNavigate();
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/notifications");
        setItems(data.notifications || []);
      } catch {}
    })();
  }, []);

  const markRead = async (id) => {
    setItems((p) => p.map((n) => (n.notif_id === id ? { ...n, read: true } : n)));
    try { await api.post(`/notifications/${id}/read`); } catch {}
  };

  const markAllRead = async () => {
    const unread = items.filter((n) => !n.read);
    setItems((p) => p.map((n) => ({ ...n, read: true })));
    await Promise.all(unread.map((n) => api.post(`/notifications/${n.notif_id}/read`).catch(() => {})));
  };

  const grouped = groupByDay(items);
  const groupKeys = Object.keys(grouped);
  const unreadCount = items.filter((n) => !n.read).length;

  return (
    <Shell section="notifications">
      <button onClick={() => navigate("/home")} className="flex items-center gap-1 text-[#4CAF50] text-sm mb-3 hover:underline">
        <ArrowLeft size={14} /> Home
      </button>

      <div className="flex items-center justify-between mb-5">
        <h1 className="text-2xl font-bold text-shadow-green">Notifications</h1>
        {unreadCount > 0 && (
          <button
            onClick={markAllRead}
            className="text-[#4CAF50] text-sm hover:underline inline-flex items-center gap-1"
            data-testid="mark-all-read"
          >
            <Check size={14} /> Mark all read
          </button>
        )}
      </div>

      {items.length === 0 ? (
        <div className="glass-card p-10 text-center">
          <Bell size={26} className="mx-auto text-[#4CAF50] mb-3" />
          <p className="text-soft">You're all caught up.</p>
        </div>
      ) : (
        groupKeys.map((day) => (
          <div key={day} className="mb-5">
            <p
              className="mb-2 px-1"
              style={{
                fontSize: 12,
                color: "var(--text-muted)",
                textTransform: "uppercase",
                letterSpacing: "0.12em",
              }}
              data-testid={`group-${day.toLowerCase()}`}
            >
              {day}
            </p>
            <div className="space-y-2">
              {grouped[day].map((n, idx) => (
                <button
                  key={n.notif_id}
                  onClick={() => {
                    markRead(n.notif_id);
                    if (n.deep_link) navigate(n.deep_link);
                  }}
                  className="w-full text-left glass-card p-4 fade-up"
                  data-testid={`notification-${idx}`}
                  style={
                    !n.read
                      ? {
                          borderLeft: "3px solid #4CAF50",
                          boxShadow: "var(--glow-green)",
                          background: "rgba(10,10,10,0.78)",
                        }
                      : { opacity: 0.78 }
                  }
                >
                  <div className="flex items-start gap-3">
                    <span
                      className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0"
                      style={{
                        background: "rgba(76,175,80,0.18)",
                        boxShadow: !n.read ? "var(--glow-green)" : "none",
                      }}
                    >
                      <Bell size={16} className="text-[#4CAF50]" />
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold">{n.title}</p>
                      <p className="text-xs text-soft mt-1" style={{ display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{n.preview}</p>
                      <p className="text-[10px] text-muted-soft mt-1">{new Date(n.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</p>
                    </div>
                    {!n.read && (
                      <span
                        className="w-2 h-2 rounded-full mt-2 flex-shrink-0"
                        style={{ background: "#4CAF50", boxShadow: "0 0 8px rgba(76,175,80,0.8)" }}
                      />
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>
        ))
      )}
    </Shell>
  );
}
